import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ConfigManagerService } from '../../shared/services/config-manager.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { RootState } from '../../store/store.state';
import {
    SetInvalidJWT,
    ValidateJWTError,
    ValidateJWTSuccess
} from '../store/auth.actions';
import { Observable } from 'rxjs/Observable';
import { selectAuthState } from '../store/auth.store';
import { AuthState, UserAuth } from '../store/auth.state';
import { AuthErrorCode } from '../../../../common/models/auth-api.interface';
import { BaseAbstractComponent } from 'quotient-ng-core';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'q-auth-frame',
    template: `
        <iframe
            style="width:0px; height:0px; position:absolute;"
            class="invisible"
            [src]="sanitizedFrameUrl"
            *ngIf="(authState$ | async).ui.showFrame"
        ></iframe>
    `
})
export class AuthFrameComponent
    extends BaseAbstractComponent
    implements OnInit {
    @Input() framed: boolean;
    private readonly frameUrl: string;
    public readonly sanitizedFrameUrl: SafeResourceUrl;
    public authState$: Observable<AuthState>;
    private AUTH_TIMEOUT_DELAY = 10;
    private authTimeout = null;
    private readonly frameMessageHandler: (
        event: MessageEvent<UserAuth>
    ) => void;

    constructor(
        public authManagerService: ConfigManagerService,
        private sanitizer: DomSanitizer,
        private store: Store<RootState>
    ) {
        super();
        this.frameUrl = `${this.authManagerService.ssoUrl}/embed/`;
        this.sanitizedFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            this.frameUrl
        );
        this.authState$ = this.store.pipe(
            takeUntil(this.$destroy),
            select(selectAuthState)
        );
        this.frameMessageHandler = this.handleFrameMessage.bind(this);
    }

    ngOnInit(): void {
        window.addEventListener('message', this.frameMessageHandler);
        this.authTimeout = setTimeout(() => {
            // If timeout is reached before a message is received then treat user as unauthenticated
            this.store.dispatch(
                new ValidateJWTError({
                    authErrorCode: AuthErrorCode.UNAUTHENTICATED,
                    framed: this.framed
                })
            );
        }, this.AUTH_TIMEOUT_DELAY * 1000);

        window['qbDebug'] = {
            invalidateJWT: (jwt: string) => {
                this.store.dispatch(new SetInvalidJWT(jwt));
            }
        };
    }

    handleFrameMessage(ev: MessageEvent<UserAuth>): void {
        if (ev && ev.origin === this.authManagerService.ssoUrl) {
            if (this.authTimeout) {
                clearTimeout(this.authTimeout);
            }
            if (!ev.data || !ev.data.jwt || !ev.data.claims) {
                this.store.dispatch(
                    new ValidateJWTError({
                        authErrorCode: AuthErrorCode.UNAUTHENTICATED,
                        framed: this.framed
                    })
                );
            } else {
                this.store.dispatch(new ValidateJWTSuccess(ev.data));
            }
        }
    }

    preOnDestroy(): any {
        window.removeEventListener('message', this.frameMessageHandler);
    }
}
